import { useQueryClient } from '@tanstack/react-query';
import { UzBadge, UzButton, UzSelect } from '@utazzo/ui';
import { RefreshCw } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  useAdPerformance,
  useMetaAdSyncStatus,
  useSyncMetaAds,
  type AdPerformanceLevel,
  type AdPerformanceRow,
} from '../meta-ads.api';

import { ReportSection } from '@/features/reports/components/ReportSection';

const inr = (n: number | null): string =>
  n === null ? '-' : `₹${n.toLocaleString('en-IN', { maximumFractionDigits: n >= 100 ? 0 : 2 })}`;
const num = (n: number): string => n.toLocaleString('en-IN');

/** First and last day of the selected months (YYYY-MM list), clipped to today. */
const rangeOf = (months: string[]): { from: string; to: string } | null => {
  if (months.length === 0) return null;
  const sorted = [...months].sort();
  const [y, m] = sorted[sorted.length - 1]!.split('-').map(Number) as [number, number];
  const lastDay = new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
  const today = new Date(Date.now() + 5.5 * 3600_000).toISOString().slice(0, 10);
  return { from: `${sorted[0]}-01`, to: lastDay < today ? lastDay : today };
};

const nameOf = (
  row: AdPerformanceRow,
  level: AdPerformanceLevel,
): { title: string; sub: string | null } => {
  if (level === 'ad')
    return {
      title: row.adName ?? row.id,
      sub: `${row.campaignName ?? ''} / ${row.adsetName ?? ''}`,
    };
  if (level === 'adset') return { title: row.adsetName ?? row.id, sub: row.campaignName };
  return { title: row.campaignName ?? row.id, sub: null };
};

/**
 * Meta ad spend imported every day, set against the leads it produced -
 * Meta's own count and the leads the CRM actually received. Replaces typing
 * spend by hand: the month-to-date adset figures below it are filled from the
 * same import.
 */
export const MetaAdPerformanceCard = ({
  months,
  canSync,
}: {
  months: string[];
  canSync: boolean;
}): React.JSX.Element => {
  const [level, setLevel] = useState<AdPerformanceLevel>('campaign');
  const range = useMemo(() => rangeOf(months), [months]);
  const status = useMetaAdSyncStatus();
  const perf = useAdPerformance(range, level);
  const sync = useSyncMetaAds();

  const s = status.data;
  const failing = s?.accounts.filter((a) => a.lastRun?.status === 'FAILED') ?? [];
  const importing = s?.running ?? false;

  // The import runs in the background: when it ends, pull the figures again.
  const qc = useQueryClient();
  const wasImporting = useRef(false);
  useEffect(() => {
    if (wasImporting.current && !importing) void qc.invalidateQueries({ queryKey: ['marketing'] });
    wasImporting.current = importing;
  }, [importing, qc]);

  return (
    <ReportSection
      title="Meta ad spend"
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-40">
            <UzSelect
              value={level}
              onValueChange={(v) => setLevel(v as AdPerformanceLevel)}
              options={[
                { value: 'campaign', label: 'By campaign' },
                { value: 'adset', label: 'By ad set' },
                { value: 'ad', label: 'By ad' },
              ]}
            />
          </div>
          {canSync && (
            <UzButton
              size="sm"
              variant="outline"
              leftIcon={<RefreshCw className="size-4" />}
              loading={sync.isPending || importing}
              disabled={!s?.enabled || importing}
              onClick={() => sync.mutate(range ?? {})}
            >
              {importing ? 'Importing' : 'Sync from Meta'}
            </UzButton>
          )}
        </div>
      }
    >
      <div className="space-y-3">
        {s && !s.enabled && (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Not connected yet. Add <code>meta.ads.config.access_token</code> (System User token with
            ads_read) and <code>meta.ads.config.account_ids</code> in Settings, Secrets. Until then,
            keep entering spend below.
          </p>
        )}
        {s?.enabled && (
          <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-600">
            {s.accounts.map((a) => (
              <UzBadge
                key={a.accountId}
                tone={a.lastRun?.status === 'FAILED' ? 'destructive' : 'secondary'}
              >
                {a.label ?? a.accountId}:{' '}
                {a.lastRun?.status === 'FAILED'
                  ? 'sync failed'
                  : a.lastSuccessAt
                    ? 'synced'
                    : 'not synced yet'}
              </UzBadge>
            ))}
            {s.latestDataDay && <span>Data up to {s.latestDataDay}</span>}
          </div>
        )}
        {failing.map((a) => (
          <p key={a.accountId} className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-800">
            {a.label ?? a.accountId}: {a.lastRun?.error}
          </p>
        ))}

        <div className="overflow-x-auto rounded-md border border-zinc-200">
          <table className="w-full min-w-[860px] text-sm">
            <thead className="bg-zinc-50 text-left text-xs text-zinc-500">
              <tr>
                <th className="px-3 py-2 font-medium">
                  {level === 'campaign' ? 'Campaign' : level === 'adset' ? 'Ad set' : 'Ad'}
                </th>
                <th className="px-3 py-2 text-right font-medium">Spend</th>
                <th className="px-3 py-2 text-right font-medium">Impressions</th>
                <th className="px-3 py-2 text-right font-medium">Clicks</th>
                <th className="px-3 py-2 text-right font-medium">CTR</th>
                <th className="px-3 py-2 text-right font-medium">Meta leads</th>
                <th className="px-3 py-2 text-right font-medium">CRM leads</th>
                <th className="px-3 py-2 text-right font-medium">Won</th>
                <th className="px-3 py-2 text-right font-medium">Cost / CRM lead</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 tabular-nums">
              {perf.isLoading && (
                <tr>
                  <td colSpan={9} className="px-3 py-6 text-center text-zinc-400">
                    Loading...
                  </td>
                </tr>
              )}
              {perf.data && perf.data.rows.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-3 py-6 text-center text-zinc-400">
                    No Meta spend imported for these months yet.
                  </td>
                </tr>
              )}
              {perf.data?.rows.map((r) => {
                const { title, sub } = nameOf(r, level);
                return (
                  <tr key={r.id}>
                    <td className="max-w-[320px] px-3 py-2">
                      <div className="truncate font-medium text-zinc-900" title={title}>
                        {title}
                      </div>
                      {sub && <div className="truncate text-[11px] text-zinc-500">{sub}</div>}
                    </td>
                    <td className="px-3 py-2 text-right">{inr(r.spend)}</td>
                    <td className="px-3 py-2 text-right">{num(r.impressions)}</td>
                    <td className="px-3 py-2 text-right">{num(r.clicks)}</td>
                    <td className="px-3 py-2 text-right">{r.ctr === null ? '-' : `${r.ctr}%`}</td>
                    <td className="px-3 py-2 text-right">{num(r.platformLeads)}</td>
                    <td className="px-3 py-2 text-right">{num(r.crmLeads)}</td>
                    <td className="px-3 py-2 text-right">{num(r.crmWon)}</td>
                    <td className="px-3 py-2 text-right font-medium">{inr(r.costPerCrmLead)}</td>
                  </tr>
                );
              })}
            </tbody>
            {perf.data && perf.data.rows.length > 0 && (
              <tfoot className="bg-zinc-50 font-semibold tabular-nums">
                <tr>
                  <td className="px-3 py-2">Total</td>
                  <td className="px-3 py-2 text-right">{inr(perf.data.totals.spend)}</td>
                  <td className="px-3 py-2 text-right">{num(perf.data.totals.impressions)}</td>
                  <td className="px-3 py-2 text-right">{num(perf.data.totals.clicks)}</td>
                  <td className="px-3 py-2 text-right" />
                  <td className="px-3 py-2 text-right">{num(perf.data.totals.platformLeads)}</td>
                  <td className="px-3 py-2 text-right">{num(perf.data.totals.crmLeads)}</td>
                  <td className="px-3 py-2 text-right">{num(perf.data.totals.crmWon)}</td>
                  <td className="px-3 py-2 text-right">{inr(perf.data.totals.costPerCrmLead)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <p className="text-xs text-zinc-500">
          Meta leads: what Meta counts. CRM leads: leads the CRM received from these ads (matched by
          campaign, ad set and ad id), created in the same days. The gap is duplicates, leads never
          forwarded, or ads sending traffic to a landing page.
        </p>
      </div>
    </ReportSection>
  );
};
